/** What one Data Upload run did (a file upload, or a client or customer documents import). */
export class ConversionDataUploadResult {
	rowsRead: number;
	inserted: number;
	updated: number;
	skipped: number;
	problems: string[];
	summary: string;
}

/** A Data Upload import running in the background on the server (polled until it is complete). */
export class ConversionDataUploadJob {
	runId: string;
	kind: string;
	fileName: string;
	startedOn: string;
	finishedOn: string;
	isComplete: boolean;
	successful: boolean;
	error: string;
	result: ConversionDataUploadResult;
}

/**
 * A client with documents in WinFactor.ClientDocuments. The Customer Documents page shows its customers in the same shape
 * (clientId = CustomerID, clientName = debtor name, factorCloudClientId = FactorCloud debtor id).
 */
export class ConversionDocumentClient {
	clientId: number;
	clientFolderName: string;
	clientName: string;
	factorCloudClientId: string;
	documentCount: number;
	sentCount: number;
	// page state
	selected?: boolean;
}

/** A customer with documents in WinFactor.CustomerDocuments (as the server returns it). */
export class ConversionDocumentCustomer {
	customerId: number;
	customerFolderName: string;
	customerName: string;
	factorCloudDebtorId: string;
	documentCount: number;
	sentCount: number;
}

/** One document of a client (or of a customer, on the Customer Documents page). */
export class ConversionDocument {
	documentKey: number;
	clientId: number;
	customerId: number;
	documentFileName: string;
	blobPath: string;
	contentType: string;
	fileSizeBytes: number;
	blobLastModifiedDate: string;
	factorCloudDocumentId: string;
	factorCloudDocumentType: string;
	sentToFactorCloudDate: string;
	/** The FactorCloud type the file name gives (NOA, UCC, CONTRACT or OTHER), used when it is sent. */
	inferredDocumentType: string;
	// page state
	selected?: boolean;
	sendStatus?: string;
	sendMessage?: string;
}

/** What happened to one document sent to FactorCloud. Status is Sent, AlreadySent or Failed. */
export class ConversionDocumentSendResult {
	documentKey: number;
	documentFileName: string;
	status: string;
	factorCloudDocumentId: string;
	message: string;
}

/** One row of WinFactor.ClientIdentifiers (the WinFactor client identifiers file). */
export class ConversionClientIdentifier {
	winFactorClientId: number;
	clientName: string;
	arDashboardAmount: number;
	clientEin: string;
	clientFileNumber: string;
	mcNumber: string;
	usDotNumber: string;
	sourceFileName: string;
	importedDate: string;
}

/** One row of the transfer terms file (FactorCloud.TermsLookup). */
export class ConversionTermsLookup {
	winFactorClientId: number;
	companyName: string;
	transferTermName: string;
	achFee: number;
	wireFee: number;
	liveTermName: string;
	sourceFileName: string;
	importedDate: string;
}

/** One WinFactor funding option (secure.WFBD). Account and routing numbers come masked (last four digits). */
export class ConversionWinFactorFundingOption {
	wfbdKey: number;
	winFactorClientId: number;
	clientName: string;
	optionNumber: number;
	optionNote: string;
	fundingType: string;
	wireType: string;
	accountName: string;
	beneficiaryName: string;
	bankAccountType: string;
	bankAccountNumber: string;
	abaRoutingNumber: string;
	bankName: string;
	bankCity: string;
	bankState: string;
	contactName: string;
	advanceDefault: boolean;
	isActive: boolean;
	factorCloudFundingInstructionId: string;
	sentToFactorCloudDate: string;
	sourceFileName: string;
	importedDate: string;
}

/** One WinFactor client/customer relationship (WinFactor.ClientDebtorRelationships). */
export class ConversionClientDebtorRelationship {
	relationshipId: number;
	clientId: number;
	clientName: string;
	clientActive: boolean;
	customerId: number;
	customerName: string;
	customerActive: boolean;
	overrideCreditLimit: number;
	overrideCreditLimitOption: number;
	relationshipDoNotBuy: boolean;
	representativeName: string;
	importedDate: string;
}

/**
 * One client name / customer name pair from the WinFactor "Active Clients with Customer Info" report
 * (WinFactor.ClientDebtorTies), with the WinFactor client / customer and Aladdin debtor it was matched to.
 */
export class ConversionClientDebtorTie {
	clientName: string;
	customerName: string;
	winFactorClientId: number;
	winFactorCustomerId: number;
	debtorKey: number;
	sourceFileName: string;
	importedDate: string;
}

/** One WinFactor client note (WinFactor.ClientNotes). */
export class ConversionWinFactorClientNote {
	clientNoteId: number;
	clientId: number;
	clientName: string;
	noteDate: string;
	userName: string;
	noteType: string;
	note: string;
}

/** One WinFactor customer note (WinFactor.CustomerNotes). */
export class ConversionWinFactorCustomerNote {
	customerNoteId: number;
	customerId: number;
	customerName: string;
	noteDate: Date;
	userName: string;
	noteType: string;
	note: string;
	factorCloudDebtorId: string;
	sentToFactorCloudDate: Date;
}

/** One customer in WinFactor.CustomerNotes, with its Aladdin / FactorCloud debtor and how many notes went across. */
export class ConversionCustomerNoteCustomer {
	customerId: number;
	customerName: string;
	noteCount: number;
	sentNoteCount: number;
	lastSentDate: Date;
	debtorKey: number;
	debtorName: string;
	factorCloudDebtorId: string;
}

/** What sending one customer's notes to FactorCloud did: Sent, AlreadySent, Skipped, NotInFactorCloud or Failed. */
export class ConversionCustomerNoteSendResult {
	customerId: number;
	customerName: string;
	factorCloudDebtorId: string;
	status: string;
	message: string;
}

/** One WinFactor invoice note (WinFactor.InvoiceNotes). */
export class ConversionWinFactorInvoiceNote {
	invoiceNoteId: number;
	clientId: number;
	clientName: string;
	invoiceNumber: string;
	loadNumber: string;
	invoiceDate: string;
	invoiceAmount: number;
	paid: boolean;
	customer: string;
	advanceNo: number;
	noteDate: string;
	userName: string;
	noteType: string;
	note: string;
	showInPortal: boolean;
	wfInvoiceId: number;
}

/** Counts shown on the Data Upload page. */
export class ConversionDataUploadSummary {
	atoBWalletCount: number;
	atoBWalletLastImportedDate: string;
	clientDocumentCount: number;
	clientDocumentClientCount: number;
	clientDocumentLastImportedDate: string;
	customerDocumentCount: number;
	customerDocumentCustomerCount: number;
	customerDocumentLastImportedDate: string;
	clientIdentifierCount: number;
	clientIdentifierLastImportedDate: string;
	termsLookupCount: number;
	termsLookupLastImportedDate: string;
	fundingOptionCount: number;
	fundingOptionLastImportedDate: string;
	relationshipCount: number;
	relationshipLastImportedDate: string;
	tieCount: number;
	tieClientCount: number;
	tieLastImportedDate: string;
	clientNoteCount: number;
	clientNoteClientCount: number;
	invoiceNoteCount: number;
	invoiceNoteClientCount: number;
	customerNoteCount: number;
	customerNoteCustomerCount: number;
	customerNoteSentCustomerCount: number;
}

/**
 * An imported AtoB wallet matched to an active WinFactor client. MatchStatus is Sent, Matched (in FactorCloud),
 * NotConverted (the conversion adds the instruction) or Ambiguous. Wallets with no match are not returned.
 */
export class ConversionAtoBWalletFunding {
	atoBId: number;
	companyName: string;
	payTo: string;
	walletStatus: string;
	matchStatus: string;
	winFactorClientId: number;
	winFactorClientName: string;
	factorCloudClientId: string;
	/** The FactorCloud client name the wallet was matched on (empty while the carrier is not in FactorCloud). */
	factorCloudClientName: string;
	candidateClients: string[];
	factorCloudFundingInstructionId: string;
	fundingInstructionSentDate: string;
	// page state
	selected?: boolean;
	sendStatus?: string;
	sendMessage?: string;
}

/** An active WinFactor client whose company name matches no AtoB wallet. */
export class ConversionWinFactorClientName {
	winFactorClientId: number;
	companyName: string;
	factorCloudClientId: string;
	factorCloudCompanyName: string;
}

/** What happened to one AtoB wallet's funding instruction. Status is Sent, AlreadySent, Adopted or Failed. */
export class ConversionAtoBFundingResult {
	atoBId: number;
	payTo: string;
	status: string;
	factorCloudClientId: string;
	factorCloudFundingInstructionId: string;
	message: string;
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/models/ConversionDataUpload.ts